import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ChartConfiguration } from 'chart.js';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import {
  ConfigSeguimiento,
  FilaReconquista,
  FilaTablero,
  FotoFuga,
  KpiSemana,
  PropuestaUnion,
  Semaforo,
  Semana,
  SeguimientoService,
  Tier,
} from 'src/app/shared/services/seguimiento.service';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { SeguimientoSocketService } from '../../services/seguimiento-socket.service';
import { GestionModalComponent } from './gestion-modal/gestion-modal.component';
import { ConfigModalComponent } from './config-modal/config-modal.component';

type FiltroTier = 'WHALE' | 'ALERTA' | 'TODOS';
type FiltroColor = 'TODOS' | Semaforo;

/** Las columnas por las que se puede ordenar el tablero. */
type CampoOrden =
  | 'nombre'
  | 'semana'
  | 'ventana'
  | 'base'
  | 'pct'
  | 'ops'
  | 'callado'
  | 'contacto';

/**
 * Tablero de retención de la mesa USDT.
 *
 * TRES COSAS QUE NO HACE ESTA PANTALLA, Y ES A PROPÓSITO:
 *
 * 1. **No recalcula nada.** El semáforo, la base prorrateada y los ratios
 *    llegan resueltos del backend. Si la pantalla los recalculara, habría dos
 *    verdades sobre el mismo cliente y la que se discute en la reunión sería
 *    la equivocada.
 * 2. **No sube archivos.** No hay campo de carga ni endpoint que lo reciba: el
 *    dato sale de la base en vivo. El tablero anterior vivía de pegar un export
 *    y de ahí salían tres errores — operaciones rechazadas contadas como
 *    volumen, dos formatos de export distintos, y la semana tecleada a mano.
 * 3. **No guarda nada en el navegador.** Las notas de gestión están en la base
 *    y son auditables. Antes vivían en el `localStorage` de una persona, que
 *    además podía borrarlas — y sobre eso se calculaban sus indicadores.
 *
 * La carga inicial es por HTTP y el canal en vivo sólo trae las
 * actualizaciones: si el socket no conecta, el tablero se ve igual con el
 * «datos al…» de la última foto.
 */
@Component({
  selector: 'app-seguimiento',
  templateUrl: './seguimiento.component.html',
  styleUrls: ['./seguimiento.component.sass'],
})
export class SeguimientoComponent implements OnInit, OnDestroy {
  cargando = true;
  refrescando = false;
  conectado = false;
  error: string | null = null;

  semana: Semana | null = null;
  datosAl: string | null = null;
  filas: FilaTablero[] = [];
  config: ConfigSeguimiento | null = null;

  // ---- reconquista
  fuga: FotoFuga | null = null;
  reconquista: FilaReconquista[] = [];
  cargandoReconquista = false;

  /**
   * Los datos del gráfico son CAMPOS, no getters.
   *
   * Un getter en `[data]` se evalúa en cada ciclo de detección de cambios y
   * devuelve un objeto nuevo cada vez; ng2-charts ve una referencia distinta y
   * vuelve a renderizar el gráfico entero. Con el socket del tablero emitiendo,
   * eso es un re-render permanente: parpadeo, animación que reinicia sola y el
   * canvas redimensionándose. Se calculan una vez, cuando llegan los datos.
   */
  graficoFugaStock: ChartConfiguration<'line'>['data'] = { labels: [], datasets: [] };
  graficoFugaFlujo: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };
  diasSinFoto: string[] = [];
  primerPuntoEsSiembra = false;

  private _serie: FotoFuga[] = [];
  get serie(): FotoFuga[] {
    return this._serie;
  }
  set serie(v: FotoFuga[]) {
    this._serie = v ?? [];
    this.armarGraficosDeFuga();
  }

  // ---- desempeño
  semanas: Semana[] = [];
  semanaKpiId: number | null = null;
  kpis: KpiSemana[] = [];
  metas: {
    coberturaPct: number;
    reaccionDias: number;
    recuperacionPct: number;
    usdtMes: number;
    recuperacionVentanaDias: number;
  } | null = null;
  notaDesempeno: string | null = null;
  cargandoDesempeno = false;

  // ---- filtros del tablero
  filtroTier: FiltroTier = 'WHALE';
  filtroColor: FiltroColor = 'TODOS';
  busqueda = '';
  soloConDueno = false;
  /** Sólo mi cartera: las cuentas de las que yo respondo. */
  soloMias = false;
  /** Whales todas las semanas; medios cada N semanas (la cadencia del plan). */
  soloDeEstaSemana = false;
  /**
   * Sólo los que llevan más de `SEGUIMIENTO_SILENCIO_FACTOR` veces su propia
   * cadencia sin aparecer. Es una cola de trabajo distinta de la de color: el
   * 31% de estos clientes no está en rojo, y son los que avisan temprano.
   */
  soloSilencio = false;

  /**
   * Orden del tablero. `null` es el orden que manda el backend, que ya viene
   * por USDT en riesgo — el que se lee en la reunión. Al tocar una columna se
   * arranca de mayor a menor, porque en todas estas medidas lo que se busca
   * primero es el extremo alto (más plata, más callado, peor porcentaje se ve
   * dando vuelta el orden).
   */
  ordenCampo: CampoOrden | null = null;
  ordenAsc = false;

  /** Uniones de cuenta esperando confirmación, para el aviso de arriba. */
  propuestas: PropuestaUnion[] = [];

  /** Quién soy: para «mi cartera» y para tomar cuentas. */
  miSub: string | null = null;
  miEmail: string | null = null;

  /** Los tres pasos de arranque, cuando el universo está vacío. */
  sembrando = false;
  pasoSiembra = '';

  /** Movimientos y evolución de la pestaña de desempeño. */
  puntajes: Array<{ duenoSub: string; puntaje: number; fase: 1 | 2 }> = [];
  fase: 1 | 2 = 1;
  recuperadosSemana: FilaTablero[] = [];
  caidasSemana: FilaTablero[] = [];

  private subs: Subscription[] = [];

  constructor(
    private api: SeguimientoService,
    private canal: SeguimientoSocketService,
    private dialog: MatDialog,
    private cognito: CognitoService,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.identificarme();
    await this.cargar();
    void this.conectarCanal();
  }

  /** El `sub` del operador, que es con lo que el backend marca al dueño. */
  private async identificarme(): Promise<void> {
    try {
      const session = await this.cognito.getSession();
      const payload = (session?.idToken as any)?.payload ?? {};
      this.miSub = payload.sub ?? null;
      this.miEmail = payload.email ?? null;
    } catch {
      // Sin sesión no se llega hasta acá; si pasa, «mi cartera» no aparece.
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.canal.desconectar();
  }

  // ------------------------------------------------------------- carga

  private async cargar(): Promise<void> {
    this.cargando = true;
    this.error = null;
    try {
      const [tablero, cfg, propuestas] = await Promise.all([
        this.api.semanaActual(),
        this.api.config().catch(() => null),
        this.api.propuestas().catch(() => []),
      ]);
      this.aplicarTablero(tablero.semana, tablero.clientes, tablero.datosAl);
      this.config = cfg;
      this.propuestas = propuestas;
    } catch (e: any) {
      this.error =
        e?.error?.message ??
        'No se pudo cargar el tablero. Volvé a intentar en unos segundos.';
    } finally {
      this.cargando = false;
    }
  }

  private aplicarTablero(
    semana: Semana,
    clientes: FilaTablero[],
    datosAl: string | null,
  ): void {
    this.semana = semana;
    this.filas = clientes ?? [];
    this.datosAl = datosAl ?? semana?.corridaAt ?? null;
  }

  /**
   * El botón «actualizar». Fuerza el recálculo y además descubre cuentas
   * nuevas, que es la única consulta del módulo que no puede filtrar por
   * usuario y por eso no corre en cada refresco automático.
   */
  async refrescar(): Promise<void> {
    if (this.refrescando) return;
    this.refrescando = true;
    try {
      const r = await this.api.refrescar();
      this.aplicarTablero(r.semana, r.clientes, r.datosAl);
    } catch (e: any) {
      Swal.fire({
        title: 'No se pudo actualizar',
        text: e?.error?.message ?? 'Intentá de nuevo en unos segundos.',
        icon: 'error',
        confirmButtonColor: '#2F4A2B',
      });
    } finally {
      this.refrescando = false;
    }
  }

  private async conectarCanal(): Promise<void> {
    await this.canal.conectar();

    this.subs.push(
      this.canal.estaConectado$().subscribe((c) => (this.conectado = c)),

      // Llega la semana recalculada: la pantalla no pidió nada.
      this.canal.alSemana$().subscribe((r) => {
        this.aplicarTablero(r.semana, r.clientes, r.datosAl);
      }),

      // Otro operador asignó un dueño o registró un contacto: se actualiza la
      // fila en su lugar, sin recargar la tabla ni perder el filtro.
      this.canal.alCliente$().subscribe(({ clienteId, gestion }) => {
        const fila = this.filas.find((f) => f.clienteId === clienteId);
        if (!fila || !gestion) return;
        fila.duenoSub = gestion.duenoSub;
        fila.duenoEmail = gestion.duenoEmail;
        fila.estadoGestion = gestion.estadoGestion;
        fila.ultimoContactoAt = gestion.ultimoContactoAt;
        fila.motivoCodigo = gestion.motivoCodigo;
        fila.motivoDetalle = gestion.motivoDetalle;
        fila.proximaAccion = gestion.proximaAccion;
        fila.proximaAccionAt = gestion.proximaAccionAt;
        fila.tasaPreferentePct = gestion.tasaPreferentePct;
        fila.tasaPreferenteHasta = gestion.tasaPreferenteHasta;
      }),

      this.canal.alFuga$().subscribe((foto) => (this.fuga = foto)),

      this.canal.alConfig$().subscribe(({ config, por }) => {
        this.config = config;
        Swal.fire({
          title: 'Cambió la configuración',
          text: `${por ?? 'Otro operador'} movió los parámetros del seguimiento. El semáforo de las semanas ya cerradas no cambia.`,
          icon: 'info',
          confirmButtonColor: '#2F4A2B',
        });
      }),

      // Un whale en rojo no espera a que alguien mire la tabla.
      this.canal.alAlerta$().subscribe(({ clientes }) => {
        const nombres = clientes
          .map((c) => this.filas.find((f) => f.clienteId === c.clienteId)?.nombre)
          .filter(Boolean)
          .join(', ');
        Swal.fire({
          title: clientes.length === 1 ? 'Un whale cayó en rojo' : 'Whales en rojo',
          text: nombres || 'Revisá el tablero.',
          icon: 'warning',
          confirmButtonColor: '#2F4A2B',
        });
      }),
    );
  }

  // -------------------------------------------------------- otras pestañas

  async cargarReconquista(): Promise<void> {
    if (this.cargandoReconquista) return;
    this.cargandoReconquista = true;
    try {
      const [lista, serie] = await Promise.all([
        this.api.reconquista(),
        this.api.serieFuga(90).catch(() => []),
      ]);
      this.fuga = lista.hoy;
      this.reconquista = lista.clientes ?? [];
      this.serie = serie ?? [];
    } catch (e: any) {
      Swal.fire({
        title: 'No se pudo cargar la lista de reconquista',
        text: e?.error?.message ?? '',
        icon: 'error',
        confirmButtonColor: '#2F4A2B',
      });
    } finally {
      this.cargandoReconquista = false;
    }
  }

  async cargarDesempeno(semanaId?: number): Promise<void> {
    if (this.cargandoDesempeno) return;
    this.cargandoDesempeno = true;
    try {
      const [r, semanas] = await Promise.all([
        this.api.desempeno(semanaId),
        this.semanas.length ? Promise.resolve(this.semanas) : this.api.semanas(20),
      ]);
      this.kpis = r.filas ?? [];
      this.metas = r.metas;
      this.puntajes = r.puntajes ?? [];
      this.fase = r.fase ?? 1;
      this.notaDesempeno = r.nota ?? null;
      this.semanas = semanas.filter((s) => s.estado === 'CERRADA');
      this.semanaKpiId = r.semana?.seguimientoSemanaId ?? null;

      // Los movimientos de la semana salen del detalle ya calculado: quién
      // volvió a verde y quién se cayó, con nombre. Es lo que se lee en la
      // reunión, y no hace falta recalcular nada para tenerlo.
      if (this.semanaKpiId) {
        const detalle = await this.api.semana(this.semanaKpiId).catch(() => null);
        this.recuperadosSemana = (detalle?.clientes ?? []).filter(
          (c) => c.cambio === 'RECUPERADO',
        );
        this.caidasSemana = (detalle?.clientes ?? []).filter((c) => c.cambio === 'CAYO');
      }
    } catch (e: any) {
      Swal.fire({
        title: 'No se pudo cargar el desempeño',
        text: e?.error?.message ?? '',
        icon: 'error',
        confirmButtonColor: '#2F4A2B',
      });
    } finally {
      this.cargandoDesempeno = false;
    }
  }

  alCambiarPestana(indice: number): void {
    if (indice === 1 && !this.reconquista.length) void this.cargarReconquista();
    if (indice === 2 && !this.kpis.length) void this.cargarDesempeno();
  }

  // ------------------------------------------------------------- acciones

  /** Abre la ficha: gestión, historia semanal y bitácora. */
  abrirGestion(fila: FilaTablero | FilaReconquista): void {
    const ref = this.dialog.open(GestionModalComponent, {
      width: '860px',
      // En teléfono el diálogo ocupa la pantalla: un formulario de gestión en
      // una ventanita de 300px no se puede llenar mientras se habla.
      maxWidth: '96vw',
      maxHeight: '92vh',
      panelClass: 'seg-dialogo',
      data: {
        clienteId: fila.clienteId,
        nombre: fila.nombre,
        tier: fila.tier,
        semanaId: this.semana?.seguimientoSemanaId ?? null,
        tasaPreferenteMaxPct: this.config?.tasaPreferenteMaxPct ?? 0,
      },
    });
    // No hace falta recargar: el guardado emite por el canal y la fila se
    // actualiza sola. Esto es sólo para el caso en que el canal no conectó.
    ref.afterClosed().subscribe((cambio) => {
      if (cambio && !this.conectado) void this.cargar();
    });
  }

  abrirConfig(): void {
    const ref = this.dialog.open(ConfigModalComponent, {
      width: '760px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      panelClass: 'seg-dialogo',
      data: { config: this.config },
    });
    ref.afterClosed().subscribe((cfg: ConfigSeguimiento | undefined) => {
      if (cfg) this.config = cfg;
    });
  }

  // -------------------------------------------------------------- vista

  /**
   * Toca una columna: primera vez de mayor a menor, segunda al revés, tercera
   * vuelve al orden del backend. Sin el tercer paso no hay forma de recuperar
   * la lista original sin recargar la pantalla.
   */
  ordenarPor(campo: CampoOrden): void {
    if (this.ordenCampo !== campo) {
      this.ordenCampo = campo;
      this.ordenAsc = false;
      return;
    }
    if (!this.ordenAsc) {
      this.ordenAsc = true;
      return;
    }
    this.ordenCampo = null;
    this.ordenAsc = false;
  }

  /**
   * El selector de orden de la barra de filtros. Elegir columna arranca de
   * mayor a menor igual que el encabezado; «Orden del tablero» lo devuelve al
   * que manda el backend.
   */
  fijarOrden(campo: CampoOrden | null): void {
    this.ordenCampo = campo;
    if (!campo) this.ordenAsc = false;
  }

  /** La flecha del encabezado. */
  iconoOrden(campo: CampoOrden): string {
    if (this.ordenCampo !== campo) return '';
    return this.ordenAsc ? '▲' : '▼';
  }

  /**
   * El número con el que se ordena cada columna.
   *
   * Sale de lo MISMO que se pinta en la celda —`ratioQueManda`,
   * `opsDeLaVentana`, la ventana cuando hay ventana—, no de un campo crudo: si
   * ordenara por `ratioUsdt` mientras la celda muestra `ventanaRatio`, la
   * tabla quedaría ordenada por un número que no está a la vista.
   */
  private valorDeOrden(f: FilaTablero, campo: CampoOrden): number | string | null {
    switch (campo) {
      case 'nombre':
        return f.nombre.toLowerCase();
      case 'semana':
        return f.usdt;
      case 'ventana':
        return f.ventanaDias ? f.ventanaUsdt : f.usdt;
      case 'base':
        return f.ventanaDias ? f.ventanaBaseUsdt : f.baseUsdt;
      case 'pct':
        return this.ratioQueManda(f);
      case 'ops':
        return this.opsDeLaVentana(f);
      case 'callado':
        return f.diasSinOperar;
      case 'contacto':
        return f.ultimoContactoAt ? new Date(f.ultimoContactoAt).getTime() : null;
    }
  }

  /**
   * Ordena una copia. `sort` muta el arreglo que recibe, y `this.filas` es el
   * que actualiza el socket fila por fila: ordenarlo en su lugar dejaría el
   * tablero ordenado incluso al quitar el orden.
   *
   * Las filas sin dato van siempre al final, en las dos direcciones. Un
   * cliente sin porcentaje no es «el más bajo»: es uno del que no se sabe, y
   * arriba de todo empuja fuera de la vista a los que sí hay que llamar.
   */
  private ordenar(filas: FilaTablero[]): FilaTablero[] {
    const campo = this.ordenCampo;
    if (!campo) return filas;
    const signo = this.ordenAsc ? 1 : -1;
    return [...filas].sort((a, b) => {
      const x = this.valorDeOrden(a, campo);
      const y = this.valorDeOrden(b, campo);
      if (x === null && y === null) return 0;
      if (x === null) return 1;
      if (y === null) return -1;
      if (typeof x === 'string' || typeof y === 'string') {
        return signo * String(x).localeCompare(String(y), 'es');
      }
      return signo * (x - y);
    });
  }

  get filasVisibles(): FilaTablero[] {
    const q = this.busqueda.trim().toLowerCase();
    const filtradas = this.filas.filter((f) => {
      if (this.filtroTier === 'WHALE' && f.tier !== 'WHALE') return false;
      if (
        this.filtroTier === 'ALERTA' &&
        f.semaforo !== 'ROJO' &&
        f.semaforo !== 'AMARILLO'
      ) {
        return false;
      }
      if (this.filtroColor !== 'TODOS' && f.semaforo !== this.filtroColor) return false;
      if (this.soloSilencio && !f.silencio) return false;
      if (this.soloConDueno && !f.duenoSub) return false;
      if (this.soloMias && f.duenoSub !== this.miSub) return false;
      // Cadencia del plan: los whales se revisan todas las semanas, los medios
      // cada N. La cola no se revisa uno por uno — se mira por excepción.
      if (this.soloDeEstaSemana && !this.tocaEstaSemana(f)) return false;
      if (q && !f.nombre.toLowerCase().includes(q)) return false;
      return true;
    });
    return this.ordenar(filtradas);
  }

  contar(semaforo: Semaforo): number {
    return this.filas.filter((f) => f.semaforo === semaforo).length;
  }

  /** Whales sanos sobre el total de whales: el número que mira Dirección. */
  get whalesVerdes(): number {
    return this.filas.filter((f) => f.tier === 'WHALE' && f.semaforo === 'VERDE').length;
  }

  get whalesTotal(): number {
    return this.filas.filter((f) => f.tier === 'WHALE').length;
  }

  get cumplimiento(): number | null {
    if (!this.semana || !this.semana.usdtEsperado) return null;
    return Math.round((this.semana.usdtTotal / this.semana.usdtEsperado) * 100);
  }

  claseSemaforo(s: Semaforo): string {
    return `sem-${s.toLowerCase()}`;
  }

  etiquetaSemaforo(f: FilaTablero): string {
    if (f.semaforo === 'FUGADO') return 'Perdido';
    if (f.semaforo === 'SIN_BASE') return 'Sin base';
    if (f.semaforo === 'VERDE') return 'Normal';
    if (f.semaforo === 'AMARILLO') return 'Bajó';
    // «Sin operar» se juzga sobre la ventana que pintó el color, no sobre la
    // semana: con la ventana de 28 días, no haber operado el lunes no es no
    // haber operado.
    return this.opsDeLaVentana(f) === 0 ? 'Sin operar' : 'Crítico';
  }

  /** Operaciones en la ventana que decidió el color. */
  opsDeLaVentana(f: FilaTablero): number {
    return f.ventanaDias > 0 ? f.ventanaOps : f.ops;
  }

  /**
   * Cómo se leyó el color de esta fila, en palabras. La pantalla tiene que
   * decirlo: «rojo» sobre 28 días y «rojo» sobre la semana en curso no son el
   * mismo dato, y las filas viejas siguen siendo de la regla anterior.
   */
  ventanaDe(f: FilaTablero): string {
    return f.ventanaDias > 0
      ? `últimos ${f.ventanaDias} días`
      : 'la semana en curso';
  }

  /** El encabezado: contra qué ventana está pintado el tablero que se ve. */
  get ventanaDelTablero(): string {
    const dias = this.filas.find((f) => f.ventanaDias > 0)?.ventanaDias ?? 0;
    return dias > 0 ? `últimos ${dias} días` : 'la semana en curso';
  }

  /**
   * Silencio anómalo: lleva más de N veces su propia cadencia sin aparecer.
   * Es señal distinta del color —el 31% de estos no está en rojo— y es la que
   * llega temprano.
   */
  contarSilencio(): number {
    return this.filas.filter((f) => f.silencio).length;
  }

  /**
   * La alerta es por frecuencia y el monto está sano.
   *
   * Con el modo `PEOR` el color sale del peor de los dos ratios, así que un
   * cliente que consolidó el flujo en menos operaciones más grandes se pinta
   * rojo aunque haya traído más plata que la esperada. Medido sobre la cartera
   * al 15/09: de 52 rojos, 16 lo son sólo por frecuencia y 9 de ésos están por
   * encima del 100% de su monto esperado — entre ellos los dos whales que
   * encabezan la lista por USDT en riesgo.
   *
   * No se corrige cambiando el color acá: el modo es una decisión de negocio y
   * vive en el parámetro. Lo que se corrige es que el operador vea de qué tipo
   * de alerta se trata antes de levantar el teléfono.
   */
  montoSanoMenosOps(f: FilaTablero): boolean {
    if (f.semaforo !== 'ROJO' && f.semaforo !== 'AMARILLO') return false;
    const usdt = f.ventanaDias ? f.ventanaUsdt : f.usdt;
    const base = f.ventanaDias ? f.ventanaBaseUsdt : f.baseUsdt;
    return base > 0 && usdt >= base;
  }

  /** «12 d · 4× su ritmo». Lo que hace falta para decidir a quién llamar. */
  textoSilencio(f: FilaTablero): string {
    if (f.diasSinOperar === null) return '–';
    const dias = `${f.diasSinOperar} d`;
    return f.silencioRel === null ? dias : `${dias} · ${f.silencioRel.toFixed(1)}× su ritmo`;
  }

  porcentaje(r: number | null): string {
    return r === null ? '–' : `${Math.round(r * 100)}%`;
  }

  /**
   * El ratio que decidió el color. Viene resuelto del backend en
   * `ventanaRatio`: es el de la ventana que pintó la fila, no el de la semana.
   *
   * Antes se recalculaba acá a partir de `ratioUsdt`/`ratioOps` y del modo. Con
   * el color saliendo de una ventana distinta de la semana eso daba dos
   * verdades sobre la misma fila —la pantalla mostrando un número y el semáforo
   * pintado con otro—, que es justamente lo que el módulo evita devolviendo el
   * semáforo ya resuelto.
   *
   * El cálculo viejo queda de respaldo para las filas anteriores al 15/09, que
   * tienen `ventanaDias = 0` y no traen `ventanaRatio`.
   */
  ratioQueManda(f: FilaTablero): number | null {
    if (f.ventanaDias > 0) return f.ventanaRatio;
    const modo = this.config?.semaforoModo ?? 'PEOR';
    if (modo === 'USDT') return f.ratioUsdt;
    if (modo === 'OPS') return f.ratioOps;
    const vivos = [f.ratioUsdt, f.ratioOps].filter((x): x is number => x !== null);
    return vivos.length ? Math.min(...vivos) : null;
  }

  /**
   * La serie con TODOS los días del calendario, y `null` donde el job no dejó
   * foto.
   *
   * Sin esto el gráfico toma las etiquetas de las filas que existen, así que un
   * día sin correr se dibuja como si no hubiera pasado: la serie de producción
   * no tiene el 10/09 y la curva unía el 09 con el 11 en línea recta. La
   * pendiente es justamente lo que hay que leer, y salía mal sin avisar.
   */
  private get serieCompleta(): Array<{
    fecha: string;
    foto: FotoFuga | null;
  }> {
    if (this._serie.length < 2) {
      return this._serie.map((f) => ({ fecha: f.fecha, foto: f }));
    }
    const porFecha = new Map(this._serie.map((f) => [f.fecha, f]));
    const dia = (d: string) => new Date(`${d}T12:00:00Z`);
    const salida: Array<{ fecha: string; foto: FotoFuga | null }> = [];
    const fin = dia(this._serie[this._serie.length - 1].fecha).getTime();
    for (let t = dia(this._serie[0].fecha); t.getTime() <= fin; t.setUTCDate(t.getUTCDate() + 1)) {
      const f = t.toISOString().slice(0, 10);
      salida.push({ fecha: f, foto: porFecha.get(f) ?? null });
    }
    return salida;
  }

  /**
   * Arma los dos gráficos de la lista de perdidos. Se llama al asignar `serie`,
   * y sólo entonces.
   */
  private armarGraficosDeFuga(): void {
    const dias = this.serieCompleta;
    const etiquetas = dias.map((d) => d.fecha.slice(5));

    this.diasSinFoto = dias.filter((d) => !d.foto).map((d) => d.fecha.slice(5));

    // El primer punto de la serie es la SIEMBRA, no un día de fugas: cuando el
    // módulo arrancó, el job selló de una vez a todos los que ya llevaban el
    // umbral sin operar. En producción eso son 49 «entraron» el 08/09 con un
    // stock de 49, y en el gráfico se lee como un desastre el primer día.
    const p = this._serie[0];
    this.primerPuntoEsSiembra = !!p && p.fugados > 1 && p.nuevos === p.fugados;

  /**
   * El STOCK: cuántos hay en la lista. Va solo, en su propio gráfico.
   *
   * Antes compartía eje con el movimiento diario, y ahí está el bug: el stock
   * va de 48 a 56 y los flujos de 0 a 7, así que con el eje escalado al stock
   * las dos líneas de movimiento quedaban pegadas al cero. El gráfico se llama
   * «¿la lista crece o baja?» y el movimiento era justo lo que no se veía. Dos
   * medidas de escalas distintas son dos gráficos, nunca dos ejes.
   */
    this.graficoFugaStock = {
      labels: etiquetas,
      datasets: [
        {
          label: 'En la lista',
          data: dias.map((d) => (d.foto ? d.foto.fugados : null)),
          borderColor: '#A3432B',
          backgroundColor: 'rgba(163, 67, 43, 0.12)',
          fill: true,
          // Sin curvatura: son conteos de un día, no una función continua, y la
          // tensión inventa valores entre dos días.
          tension: 0,
          pointRadius: 2.5,
          pointBackgroundColor: '#A3432B',
          spanGaps: false,
        },
      ],
    };

  /**
   * El MOVIMIENTO: entraron hacia arriba, volvieron hacia abajo.
   *
   * Barras y no líneas porque son cuentas de un día, y divergentes porque la
   * pregunta es el neto: el lado de la barra ya contesta si la lista creció o
   * bajó, sin depender del color. Eso importa además porque rojo contra verde
   * no se distingue con daltonismo — acá la posición lleva el dato y el color
   * sólo lo refuerza.
   */
    this.graficoFugaFlujo = {
      labels: etiquetas,
      datasets: [
        {
          label: 'Entraron',
          data: dias.map((d) => (d.foto ? d.foto.nuevos : null)),
          backgroundColor: '#A3432B',
        },
        {
          label: 'Volvieron',
          data: dias.map((d) => (d.foto ? -d.foto.recuperados : null)),
          backgroundColor: '#2B5C9E',
        },
      ],
    };
  }

  readonly opcionesGrafico: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
      x: { grid: { display: false } },
    },
    // Una serie sola no necesita caja de leyenda: el título ya la nombra.
    plugins: { legend: { display: false } },
  };

  /**
   * Las barras del movimiento. «Volvieron» viaja negativo para que quede del
   * otro lado del cero, así que el eje y el tooltip muestran el valor absoluto:
   * nadie volvió −1 vez.
   */
  readonly opcionesGraficoFlujo: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      y: {
        ticks: {
          precision: 0,
          callback: (v) => Math.abs(Number(v)).toString(),
        },
        grid: { color: (ctx) => (ctx.tick.value === 0 ? '#9ca3af' : '#e5e7eb') },
      },
      x: { grid: { display: false } },
    },
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12 } },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            `${ctx.dataset.label}: ${Math.abs(Number(ctx.parsed.y))}`,
        },
      },
    },
  };

  get kpiGlobal(): KpiSemana | null {
    return this.kpis.find((k) => k.duenoSub === 'GLOBAL') ?? null;
  }

  get kpisPorDueno(): KpiSemana[] {
    return this.kpis.filter((k) => k.duenoSub !== 'GLOBAL');
  }

  cumpleMeta(valor: number | null, meta: number, menorEsMejor = false): boolean | null {
    if (valor === null) return null;
    return menorEsMejor ? valor <= meta : valor >= meta;
  }

  nombreDueno(k: KpiSemana): string {
    if (k.duenoSub === 'SIN_DUENO') return 'Sin dueño';
    return k.duenoEmail ?? k.duenoSub;
  }

  /** El puntaje ponderado de un dueño, si el backend lo calculó. */
  puntajeDe(duenoSub: string): number | null {
    return this.puntajes.find((p) => p.duenoSub === duenoSub)?.puntaje ?? null;
  }

  // ------------------------------------------------------------- arranque

  /**
   * El universo está vacío: hay clientes en la base pero nadie sembrado
   * todavía. Sin esto, el tablero se ve en cero y parece roto cuando en
   * realidad falta el arranque.
   */
  get universoVacio(): boolean {
    return !this.cargando && !!this.semana && this.filas.length === 0;
  }

  /**
   * Los tres pasos de puesta en marcha, en orden y con lo que devolvió cada
   * uno. Son idempotentes: si algo sale mal se puede repetir.
   */
  async arrancar(): Promise<void> {
    const ok = await Swal.fire({
      title: '¿Arrancar el seguimiento?',
      html:
        '<div style="text-align:left;font-size:.9rem">Se hacen tres cosas, en este orden:' +
        '<ol style="margin:.5rem 0 0 1rem;padding:0">' +
        '<li>Armar el universo: los titulares y sus cuentas, unidos por documento.</li>' +
        '<li>Calcular cuánto valía cada uno cuando estaba entero (enero–agosto).</li>' +
        '<li>Calcular el nivel normal vigente y los segmentos.</li>' +
        '</ol><p style="margin:.6rem 0 0">No borra nada y se puede repetir.</p></div>',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Arrancar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#2F4A2B',
    });
    if (!ok.isConfirmed) return;

    this.sembrando = true;
    try {
      this.pasoSiembra = 'Armando el universo de clientes…';
      const identidad = await this.api.sembrarIdentidad();

      this.pasoSiembra = 'Calculando cuánto valía cada cliente…';
      await this.api.sembrarReferencia();

      this.pasoSiembra = 'Calculando el nivel normal y los segmentos…';
      const base = await this.api.sembrarBaseVigente();

      this.pasoSiembra = 'Recalculando la semana…';
      await this.api.refrescar();
      await this.cargar();

      await Swal.fire({
        title: 'Seguimiento en marcha',
        html:
          '<div style="text-align:left;font-size:.9rem">' +
          `<div><b>${identidad.titularesCreados}</b> titulares nuevos, ` +
          `<b>${identidad.cuentasAtadas}</b> cuentas atadas` +
          (identidad.propuestasPorNombre
            ? `, <b>${identidad.propuestasPorNombre}</b> uniones por confirmar`
            : '') +
          '</div>' +
          `<div><b>${base?.whales ?? 0}</b> whales · <b>${base?.medios ?? 0}</b> medios · ` +
          `<b>${base?.cola ?? 0}</b> cola</div></div>`,
        icon: 'success',
        confirmButtonColor: '#2F4A2B',
      });
    } catch (e: any) {
      Swal.fire({
        title: 'No se pudo arrancar',
        text: e?.error?.message ?? 'Revisá el log del servidor.',
        icon: 'error',
        confirmButtonColor: '#2F4A2B',
      });
    } finally {
      this.sembrando = false;
      this.pasoSiembra = '';
    }
  }

  // ------------------------------------------------- uniones por confirmar

  /**
   * Resuelve las uniones propuestas por nombre, una por una.
   *
   * Se confirman a mano a propósito: unir dos homónimos mezcla el volumen de
   * dos clientes distintos, y en el tablero eso se ve como una caída que no
   * existió. Por eso la pantalla muestra los dos documentos y los dos nombres
   * antes de preguntar.
   */
  async resolverPropuestas(clienteId?: number): Promise<void> {
    const pendientes = clienteId
      ? this.propuestas.filter((p) => p.clienteId === clienteId)
      : this.propuestas;

    if (!pendientes.length) {
      await Swal.fire({
        title: 'No hay uniones pendientes',
        icon: 'info',
        confirmButtonColor: '#2F4A2B',
      });
      return;
    }

    for (const p of pendientes) {
      const r = await Swal.fire({
        title: '¿Es la misma persona?',
        html:
          '<div style="text-align:left;font-size:.9rem">' +
          `<div><b>Titular:</b> ${p.titular} · doc ${p.documentoTitular ?? 'sin documento'}</div>` +
          `<div><b>Cuenta nueva:</b> ${p.nombreEnLaCuenta ?? '(sin nombre)'} · doc ${p.documentoEnLaCuenta ?? 'sin documento'}</div>` +
          `<div style="color:#6b7280;margin-top:.4rem">${p.email ?? ''}</div>` +
          '<p style="margin:.6rem 0 0">Se propuso por <b>nombre</b>, no por documento. ' +
          'Si son dos personas distintas, rechazá: unirlas mezcla el volumen de ambas.</p></div>',
        icon: 'question',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: 'Es la misma',
        denyButtonText: 'Son distintas',
        cancelButtonText: 'Dejar para después',
        confirmButtonColor: '#2F4A2B',
        denyButtonColor: '#A3432B',
      });

      if (r.isDismissed) break;
      try {
        await this.api.resolverPropuesta(p.cuentaId, r.isConfirmed);
      } catch (e: any) {
        Swal.fire({
          title: 'No se pudo resolver',
          text: e?.error?.message ?? '',
          icon: 'error',
          confirmButtonColor: '#2F4A2B',
        });
        break;
      }
    }

    await this.cargar();
  }

  // ---------------------------------------------------------- la cadencia

  /**
   * Si a este cliente le toca revisión esta semana. Los whales, todas; los
   * medios, cada `cadenciaMedioSemanas`; la cola no se revisa uno por uno —se
   * mira por excepción, y para eso están los filtros de color.
   */
  tocaEstaSemana(f: FilaTablero): boolean {
    if (f.tier === 'WHALE') return true;
    if (f.tier !== 'MEDIO') return false;
    const cada = this.config?.cadenciaMedioSemanas ?? 2;
    if (cada <= 1) return true;
    return this.indiceDeSemana % cada === 0;
  }

  /** Cuántas semanas van desde el arranque del programa. */
  private get indiceDeSemana(): number {
    if (!this.semana || !this.config) return 0;
    const a = new Date(`${this.config.semanasDesde}T12:00:00Z`).getTime();
    const b = new Date(`${this.semana.fechaInicio}T12:00:00Z`).getTime();
    return Math.max(0, Math.round((b - a) / (7 * 86400000)));
  }
}
